import { Modal } from '@/modules/common/components/Modal';
import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { PaymentPayMethod } from '@/types/payment';
import { yupResolver } from '@hookform/resolvers/yup';
import { CreditCardOutlined, SwapHorizOutlined } from '@mui/icons-material';
import {
  Button,
  FormControl,
  FormControlLabel,
  ModalProps,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

type ConfirmPaymentsModalProps = Omit<
  ModalProps,
  'title' | 'subTitle' | 'onClose' | 'actions' | 'children'
> & {
  paymentIds: string[];
  totalToPay: number;
  onClose: (values?: ConfirmPaymentsFormValues) => Promise<void>;
};

export type ConfirmPaymentsFormValues = {
  paymentMethod: PaymentPayMethod;
  paymentIds: string[];
};

export function ConfirmPaymentsModal({
  paymentIds,
  totalToPay,
  onClose,
  ...props
}: ConfirmPaymentsModalProps) {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<ConfirmPaymentsFormValues>({
    values: {
      paymentMethod: PaymentPayMethod.Card,
      paymentIds,
    },
    defaultValues: {
      paymentMethod: PaymentPayMethod.Card,
      paymentIds: [],
    },
    resolver: yupResolver(
      yup.object({
        paymentMethod: yup
          .string()
          .oneOf(Object.values(PaymentPayMethod))
          .required(),
        paymentIds: yup.array().of(yup.string().uuid().required()).required(),
      }),
    ),
  });

  useEffect(() => {
    reset();
  }, [props.open, reset]);

  return (
    <Modal
      {...props}
      onClose={() => onClose()}
      title={`You’re about to proceed with a ${formatCurrencyValue(totalToPay.toString(), 'EUR')} payment. Confirm?`}
      subTitle={`Once confirmed, ${paymentIds.length > 1 ? 'these payments' : 'this payment'} cannot be undone. Please ensure all payment details are accurate before proceeding.`}
      showCloseButton
      classes={{
        paper: 'max-w-2xl',
      }}
      actions={
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outlined"
            onClick={() => onClose()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="confirm-payments-form"
            variant="contained"
            loading={isSubmitting}
          >
            Continue to pay
          </Button>
        </div>
      }
    >
      <form
        id="confirm-payments-form"
        onSubmit={handleSubmit(onClose)}
        className="flex flex-col gap-4"
      >
        <Typography variant="body2" className="font-semibold">
          Select your preferred payment method:
        </Typography>
        <Controller
          name="paymentMethod"
          control={control}
          render={({ field }) => (
            <RadioGroup {...field} className="flex flex-col gap-4">
              {[
                {
                  value: PaymentPayMethod.Card,
                  icon: <CreditCardOutlined />,
                  title: 'Credit Card',
                  description: 'Pay instantly with your credit or debit card',
                },
                {
                  value: PaymentPayMethod.BankTransfer,
                  icon: <SwapHorizOutlined />,
                  title: 'Bank Transfer',
                  description:
                    'Direct transfer from your bank account (1-3 business days)',
                },
              ].map(({ value, icon, title, description }) => (
                <FormControl
                  key={value}
                  className={`p-4 flex flex-col gap-4 border border-solid rounded-lg ${field.value === value ? 'border-info bg-info-light/50' : 'border-divider-light'}`}
                >
                  <FormControlLabel
                    control={
                      <Radio
                        icon={icon}
                        checkedIcon={icon}
                        value={value}
                        title={title}
                      />
                    }
                    label={
                      <div className="flex flex-col w-full">
                        <Typography variant="h5">{title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {description}
                        </Typography>
                      </div>
                    }
                  />
                </FormControl>
              ))}
            </RadioGroup>
          )}
        />
      </form>
    </Modal>
  );
}
